import { type ExamCourse } from '../../constants/types';
import {
  type ExamCourseWithDuringAndPostExamSettings,
  type DuringAndPostExamSettingsSectionFormInputs,
} from '../../containers/during-and-post-exam-settings-section-form/types';

type ExamCourseSettings = ExamCourse['settings'];

const passedTemplateMap = {
  AVERAGE_SCORE_BY_PART: 'average-score-by-part',
  AVERAGE_SCORE_BY_SUBJECT: 'average-score-by-subject',
  NO_SCORE: 'no-score',
  SECTION_SCORE: 'section-score',
} as const;

const failedTemplateMap = {
  AVERAGE_SCORE_BY_PART: 'average-score-by-part',
  AVERAGE_SCORE_BY_SUBJECT: 'average-score-by-subject',
  NO_SCORE: 'no-score',
  SECTION_SCORE: 'section-score',
} as const;

const averageScoreConditionMap = {
  ALL_CENTERS: 'all-centers',
  EACH_CENTER: 'each-center',
} as const;

const QUESTIONNAIRE_TEMPLATE_MAP = {
  TEMPLATE_A: 'template-a',
  TEMPLATE_B: 'template-b',
  TEMPLATE_C: 'template-c',
} as const;

function mapDeliveryTime(
  data: NonNullable<ExamCourseSettings['certificate']>['delayedDays'] | undefined,
) {
  if (data === null || data === undefined) {
    return undefined;
  } else if (data === 0) {
    return 'immediately';
  }
  return 'delay';
}

function getFileNameFromKey(key: string) {
  // the key is in the format of "some/path/to/originalFileName.ext"
  const parts = key.split('/');
  return parts.pop() || key;
}

export default function mapExamCourseSettingsToDuringAndPostExamSettingsFormData(
  data: ExamCourseWithDuringAndPostExamSettings['settings'] | undefined,
): DuringAndPostExamSettingsSectionFormInputs {
  const attachDocumentFile =
    data?.attachDocument?.fileName && data?.attachDocument?.key
      ? {
          name: data.attachDocument.fileName,
          uploadFileName: getFileNameFromKey(data.attachDocument.key),
        }
      : null;
  return {
    questionnaire: {
      enabled: data?.questionnaire?.templateId ? 'true' : 'false',
      templateId: data?.questionnaire?.templateId
        ? (QUESTIONNAIRE_TEMPLATE_MAP[data.questionnaire.templateId] ?? null)
        : null,
    },
    resultDisplay: {
      shouldDisplay: data?.resultDisplay?.examResult ? 'true' : 'false',
    },
    resultSend: {
      portal: data?.resultSend?.portal ? 'true' : 'false',
    },
    certificate: {
      shouldIssue: data?.certificate ? 'true' : 'false',
      template: {
        passTemplate: data?.certificate?.passedTemplate
          ? passedTemplateMap[data?.certificate.passedTemplate]
          : '',
        failTemplate: data?.certificate?.failedTemplate
          ? failedTemplateMap[data?.certificate.failedTemplate]
          : '',
      },
      averageScoreCondition: data?.certificate?.averageScoreCondition
        ? averageScoreConditionMap[data?.certificate.averageScoreCondition]
        : undefined,
      deliveryTime: {
        type: mapDeliveryTime(data?.certificate?.delayedDays),
        delay:
          data?.certificate?.delayedDays && data?.certificate?.delayedDays > 0
            ? data?.certificate?.delayedDays
            : undefined,
      },
      expiry: {
        type: data?.certificate?.validityDurationYears ? 'delay' : 'none',
        delay:
          data?.certificate?.validityDurationYears && data?.certificate?.validityDurationYears > 0
            ? data?.certificate?.validityDurationYears
            : undefined,
      },
    },
    attachDocument: {
      enabled: data?.attachDocument ? 'true' : 'false',
      file: attachDocumentFile,
    },
  };
}
